/**
 * ConnectionsPanel — relationship graph dla tendera.
 *
 * Pokazuje nodes (authority, board members, architects, contractors, suppliers)
 * z edges. Wszystko z embedded MongoDB refs + SPHINX intel enrichments.
 *
 * Lightweight tree-style view (bez ciężkich graph libs jak react-flow). Każdy
 * branch rozwijalny. Nodes klikalne — otwierają IntelDialog.
 */

import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Skeleton } from '../ui/skeleton';
import {
  Building2, Users, UserCheck, Truck, Briefcase, FileText,
  Network, RefreshCw, AlertTriangle, ShieldCheck
} from 'lucide-react';
import { tenderConnections } from '../../lib/api';

const t = (lang, pl, en) => (lang === 'pl' ? pl : en);

const TYPE_CONFIG = {
  tender:     { icon: FileText,   label: 'Przetarg',    color: 'text-primary' },
  authority:  { icon: Building2,  label: 'Zamawiający', color: 'text-blue-300' },
  person:     { icon: UserCheck,  label: 'Osoba',       color: 'text-purple-300' },
  architect:  { icon: Briefcase,  label: 'Projektant',  color: 'text-amber-300' },
  contractor: { icon: Users,      label: 'Kontrahent',  color: 'text-cyan-300' },
  supplier:   { icon: Truck,      label: 'Dostawca',    color: 'text-emerald-300' },
};

const RISK_COLOR = {
  green: 'bg-emerald-500/15 text-emerald-300',
  amber: 'bg-amber-500/15 text-amber-300',
  red:   'bg-red-500/15 text-red-300',
  unknown: 'bg-zinc-500/15 text-zinc-400',
};

export default function ConnectionsPanel({ oppId, lang = 'pl' }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { load(); }, [oppId]); // eslint-disable-line

  async function load() {
    setLoading(true);
    setError(null);
    try {
      const r = await tenderConnections(oppId);
      setData(r);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error loading connections');
    }
    setLoading(false);
  }

  if (loading) {
    return (
      <Card><CardContent className="p-6">
        <div className="space-y-2">
          <Skeleton className="h-6 w-1/2" />
          <Skeleton className="h-4 w-3/4" />
          <Skeleton className="h-4 w-2/3" />
        </div>
      </CardContent></Card>
    );
  }

  if (error) {
    return (
      <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">
        <AlertTriangle className="w-8 h-8 mx-auto mb-2 text-amber-500/40" />
        {error}
      </CardContent></Card>
    );
  }

  if (!data || data.nodes.length <= 1) {
    return (
      <Card><CardContent className="p-6 text-center text-sm text-muted-foreground">
        <Network className="w-8 h-8 mx-auto mb-2 text-muted-foreground/40" />
        {t(lang, 'Brak powiązań zidentyfikowanych. Wzbogać autoryzację, dodaj RFQ lub kontrahentów aby zobaczyć graf.',
              'No connections identified yet. Enrich authority, add RFQ or contractors to populate.')}
      </CardContent></Card>
    );
  }

  // Group nodes by type
  const byType = {};
  for (const n of data.nodes) {
    if (n.id === data.central_node_id) continue;
    byType[n.type] = byType[n.type] || [];
    byType[n.type].push(n);
  }

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Network className="w-4 h-4 text-primary" />
            {t(lang, 'Sieć powiązań', 'Relationships')}
            <Badge variant="outline" className="ml-2">{data.stats.total_nodes - 1} {t(lang, 'powiązań', 'connections')}</Badge>
          </CardTitle>
          <Button size="sm" variant="ghost" onClick={load}><RefreshCw className="w-3.5 h-3.5" /></Button>
        </div>
      </CardHeader>
      <CardContent>
        {/* Stats strip */}
        <div className="flex flex-wrap gap-2 mb-4 text-xs">
          {Object.entries(byType).map(([type, nodes]) => {
            const cfg = TYPE_CONFIG[type] || {};
            const Icon = cfg.icon || Network;
            return (
              <Badge key={type} variant="outline" className={`gap-1 ${cfg.color}`}>
                <Icon className="w-3 h-3" />
                {cfg.label || type}: {nodes.length}
              </Badge>
            );
          })}
        </div>

        {/* Central node */}
        {(() => {
          const central = data.nodes.find(n => n.id === data.central_node_id);
          if (!central) return null;
          return (
            <div className="mb-4 p-3 bg-primary/5 border border-primary/20 rounded-lg">
              <div className="flex items-center gap-2">
                <FileText className="w-4 h-4 text-primary" />
                <div className="flex-1">
                  <div className="font-medium text-sm">{central.label}</div>
                  <div className="text-xs text-muted-foreground">
                    {central.value_pln ? `${Math.round(central.value_pln).toLocaleString('pl-PL')} PLN` : '—'}
                    {central.stage && ` · stage: ${central.stage}`}
                    {central.deadline && ` · termin: ${central.deadline.slice(0, 10)}`}
                  </div>
                </div>
              </div>
            </div>
          );
        })()}

        {/* Groups */}
        <div className="space-y-4">
          {['authority', 'person', 'architect', 'contractor', 'supplier'].map((type) => {
            const nodes = byType[type] || [];
            if (nodes.length === 0) return null;
            const cfg = TYPE_CONFIG[type];
            const Icon = cfg.icon;
            return (
              <div key={type}>
                <div className={`text-xs uppercase tracking-wide mb-1.5 flex items-center gap-1.5 ${cfg.color}`}>
                  <Icon className="w-3.5 h-3.5" />
                  {cfg.label} ({nodes.length})
                </div>
                <div className="space-y-1">
                  {nodes.map((n) => (
                    <NodeRow key={n.id} node={n} lang={lang} />
                  ))}
                </div>
              </div>
            );
          })}
        </div>

        {/* Trace */}
        <div className="mt-4 text-[10px] text-muted-foreground/50 text-right">
          Source: MASKIL embedded refs + SPHINX intel enrichment
        </div>
      </CardContent>
    </Card>
  );
}

function NodeRow({ node, lang }) {
  const cfg = TYPE_CONFIG[node.type] || {};
  const Icon = cfg.icon || Network;

  const subInfo = [];
  if (node.nip) subInfo.push(`NIP: ${node.nip}`);
  if (node.krs) subInfo.push(`KRS: ${node.krs}`);
  if (node.role) subInfo.push(node.role);
  if (node.rfq_status) subInfo.push(`RFQ: ${node.rfq_status}`);
  if (node.project_count) subInfo.push(`${node.project_count} projektów`);

  return (
    <div className="p-2 rounded-md bg-muted/20 hover:bg-muted/40 transition-colors">
      <div className="flex items-center gap-2">
        <Icon className={`w-3.5 h-3.5 ${cfg.color || ''}`} />
        <div className="flex-1 min-w-0">
          <div className="text-sm truncate">{node.label}</div>
          {subInfo.length > 0 && (
            <div className="text-[11px] text-muted-foreground truncate">{subInfo.join(' · ')}</div>
          )}
        </div>
        <div className="flex gap-1 flex-shrink-0">
          {node.risk_level && node.risk_level !== 'unknown' && (
            <Badge className={`text-[10px] ${RISK_COLOR[node.risk_level] || ''}`}>
              {node.risk_level === 'green' ? <ShieldCheck className="w-3 h-3" /> :
               node.risk_level === 'red' ? <AlertTriangle className="w-3 h-3" /> :
               null}
              <span className="ml-1">{node.risk_level}</span>
            </Badge>
          )}
          {node.sanctions_match && (
            <Badge className="text-[10px] bg-red-500/15 text-red-300">
              ⚠ SANCTIONS
            </Badge>
          )}
          {node.is_pep && (
            <Badge className="text-[10px] bg-purple-500/15 text-purple-300">PEP</Badge>
          )}
          {node.enriched && (
            <Badge variant="outline" className="text-[10px]">✓ intel</Badge>
          )}
        </div>
      </div>
    </div>
  );
}
